import humps from 'lodash-humps-ts';
import { snakes } from '@utils/lodash';
import { authAxios } from '@components/Auth';
import { CorporateHealthQuote } from '@models/CorporateHealthQuote';
import { API_V1_BASE } from '@config';
import { NumericString, Numeric, WithParams } from '@utils/typescript';
import { paginate, Pagination, PaginationParams } from '@models/Pagination';
import { CorporateUser } from '@models/User';
import { ImportModel, ImportTypes } from '@models/ImportModel';

export type CreateCorporateHealthQuoteParams = WithParams<{
  insurer_id: Numeric;
  health_plan_id: Numeric;
  user_id: Numeric;
  third_party_admin_email?: string;
  activation_date: string;
  admin_fee_amount: NumericString;
  note: string;
  proof: File | undefined;
}>;

export type GetCorporateHealthQuoteParams = WithParams<{
  order_number: string;
}>;

export type UpdateCorporateHealthQuoteParams = GetCorporateHealthQuoteParams & CreateCorporateHealthQuoteParams;

export type LockCorporateHealthQuoteParams = GetCorporateHealthQuoteParams & Pick<CreateCorporateHealthQuoteParams, 'activationDate'>;

export type PayByCashCorporateHealthQuoteParams = GetCorporateHealthQuoteParams & WithParams<{
  proof: File | undefined;
}>;

export type GetUsersCorporateHealthQuoteParams = GetCorporateHealthQuoteParams & PaginationParams & WithParams<{
  search?: string;
}>;

export type UpdateUserCorporateHealthQuoteParams = GetCorporateHealthQuoteParams & WithParams<{
  user_id: number;
  name: string;
  dob: string;
  gender: string;
}>;

export type DeleteUserCorporateHealthQuoteParams = GetCorporateHealthQuoteParams & WithParams<{
  user_id: number;
}>;

export type GetImportsCorporateHealthQuoteParams = GetCorporateHealthQuoteParams;

export type CreateImportCorporateHealthQuoteParams = GetCorporateHealthQuoteParams & WithParams<{
  agree: boolean;
  dataCsv: File | undefined;
  importType: ImportTypes | '';
  proof?: File;
}>;

export type ExportActiveUsersCorporateHealthQuoteParams = GetCorporateHealthQuoteParams;

export class CorporateHealthQuoteApi {
  static create = async (params: CreateCorporateHealthQuoteParams) => {
    return await authAxios
      .postForm(`${API_V1_BASE}/user/corporate_health_quotes`, snakes(params))
      .then((response) => humps(response.data) as CorporateHealthQuote);
  };

  static get = async ({ orderNumber }: GetCorporateHealthQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}`)
      .then((response) => humps(response.data) as CorporateHealthQuote);
  };

  static update = async ({ orderNumber, ...rest }: GetCorporateHealthQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}`, snakes(rest))
      .then((response) => humps(response.data) as CorporateHealthQuote);
  };

  static lock = async ({ orderNumber, ...rest }: LockCorporateHealthQuoteParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/lock`, snakes(rest));
  };

  static payByCash = async ({ orderNumber, ...rest }: PayByCashCorporateHealthQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/pay_by_cash`, snakes(rest));
  };

  static getUsers = async ({ orderNumber, ...rest }: GetUsersCorporateHealthQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/users`, { params: snakes(rest) })
      .then((response) => paginate(response) as [CorporateUser[], Pagination]);
  };

  static updateUser = async ({ orderNumber, userId, ...rest }: UpdateUserCorporateHealthQuoteParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/users/${userId}`, snakes(rest))
      .then((response) => humps(response.data) as CorporateUser);
  };

  static deleteUser = async ({ orderNumber, userId }: DeleteUserCorporateHealthQuoteParams) => {
    return await authAxios
      .delete(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/users/${userId}`);
  };

  static getImports = async ({ orderNumber, ...rest }: GetImportsCorporateHealthQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/imports`, { params: snakes(rest) })
      .then((response) => humps(response.data) as ImportModel[]);
  };

  static createImport = async ({ orderNumber, ...rest }: CreateImportCorporateHealthQuoteParams) => {
    return await authAxios
      .postForm(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/imports`, snakes(rest))
      .then((response) => humps(response.data) as ImportModel[]);
  };

  static ExportActiveUsers = async ({ orderNumber }: ExportActiveUsersCorporateHealthQuoteParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/corporate_health_quotes/${encodeURIComponent(orderNumber)}/export_active_users`);
  };
}
